/**
 * Etapa 1 — Identificação: de quem é a instalação, o que ela faz e em que estado de uso está.
 */
import React, { useEffect } from 'react';
import { Alert, Box, Button, CircularProgress, Grid, MenuItem, TextField, Typography } from '@mui/material';
import { useCompanyList } from '../../../../../stores/companyStore';
import { useManifestoStore } from '../../../../../stores/manifestoStore';
import { CompanySelectField } from '../../../../../components/forms';
import {
  FLAMMABLE_ACTIVITY_TYPES,
  FLAMMABLE_INSTALLATION_STATUSES,
  ROTULO_DA_ATIVIDADE,
  ROTULO_DO_STATUS_DA_INSTALACAO,
  type FlammableActivityType,
  type FlammableInstallationStatus,
} from '../../../../../types/flammableInstallation';
import type { Department } from '../../../../../types/department';
import { ClassChip } from '../../FlammableChips';
import { motivoDaClasse } from '../../flammableTexts';
import type { IdentificationErrors, IdentificationValues } from '../FlammableInstallationWizard.types';

const fieldSx = { '& .MuiFormHelperText-root': { fontSize: '1rem' } };

interface IdentificationStepProps {
  values: IdentificationValues;
  errors: IdentificationErrors;
  departments: Department[];
  /** A empresa que aparece (desabilitada) quando a tela não a pergunta: a do contexto ou a da instalação. */
  shownCompanyId: string;
  /** Pergunta a empresa dona: só na criação, quando o cabeçalho não tem empresa ativa. */
  askCompany?: boolean;
  /** Instalação já salva: a empresa aparece, mas não muda mais. */
  companyLocked?: boolean;
  /** Modo leitura (sem `.update`) ou gravação em andamento: nenhum campo aceita digitação. */
  disabled?: boolean;
  onChange: (patch: Partial<IdentificationValues>) => void;
}

export const IdentificationStep: React.FC<IdentificationStepProps> = ({
  values,
  errors,
  departments,
  shownCompanyId,
  askCompany = false,
  companyLocked = false,
  disabled = false,
  onChange,
}) => {
  const companyList = useCompanyList();
  const asking = askCompany && !companyLocked;
  const companyListFailed = asking && companyList.carregada && Boolean(companyList.error);
  const companyListLoading = asking && !companyList.carregada;
  const shownCompany = companyList.companies.find((company) => String(company.id) === shownCompanyId);
  const showFixedCompany = !asking && shownCompanyId !== '';

  // A lista de empresas só é buscada pelo seletor; sem ele (empresa do contexto, ou instalação já
  // salva), o nome real precisa ser pedido aqui. Sem permissão, o nome vem do seletor de contexto.
  const { carregada, isLoading, loadCompanies } = companyList;
  useEffect(() => {
    if (showFixedCompany && !carregada && !isLoading) {
      loadCompanies();
    }
  }, [showFixedCompany, carregada, isLoading, loadCompanies]);

  const contextos = useManifestoStore((estado) => estado.manifesto?.contextos);
  const nomeNoContexto = contextos?.find(
    (contexto) => contexto.tipo === 'empresa' && String(contexto.id) === shownCompanyId,
  )?.nome;
  const shownCompanyName =
    shownCompany?.tradeName || shownCompany?.legalName || nomeNoContexto || 'Empresa selecionada';

  // Escolhida a empresa, só os setores dela servem; sem escolha (empresa ativa), vale a lista toda.
  const companyDepartments = values.companyId
    ? departments.filter((department) => String(department.companyId) === values.companyId)
    : departments;

  const { classification } = values;

  return (
    <Box>
      <Typography variant="body1" sx={{ mb: 3 }}>
        Diga qual é a instalação e o que ela faz. O nome e a atividade são obrigatórios; o resto você pode completar
        depois.
      </Typography>

      <Typography variant="h6" component="h3" sx={{ mb: 2 }}>
        Qual é a instalação
      </Typography>
      <Grid container spacing={3} sx={{ mb: 4 }}>
        {companyListLoading && (
          <Grid size={{ xs: 12, md: 6 }} sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <CircularProgress size={24} aria-label="Carregando as empresas" />
            <Typography variant="body1">Carregando as empresas...</Typography>
          </Grid>
        )}
        {companyListFailed && (
          <Grid size={{ xs: 12, md: 6 }}>
            <Alert
              severity="error"
              action={
                <Button color="inherit" onClick={companyList.loadCompanies} sx={{ minHeight: 44 }}>
                  Tentar de novo
                </Button>
              }
            >
              Não foi possível carregar as empresas. Sem elas não dá para escolher a dona da instalação.
            </Alert>
          </Grid>
        )}
        {showFixedCompany && (
          <Grid size={{ xs: 12, md: 6 }}>
            <TextField
              fullWidth
              label="Empresa proprietária"
              value={shownCompanyName}
              disabled
              helperText="Não muda depois de criada"
              sx={fieldSx}
            />
          </Grid>
        )}
        {asking && !companyListFailed && (
          <CompanySelectField
            grid={{ xs: 12, md: 6 }}
            required
            label="Empresa proprietária"
            value={values.companyId}
            onChange={(companyId) => onChange({ companyId, departmentId: '' })}
            error={Boolean(errors.companyId)}
            helperText={errors.companyId || 'A instalação entra no prontuário desta empresa.'}
            disabled={disabled}
          />
        )}
        <Grid size={{ xs: 12, md: 6 }}>
          <TextField
            select
            fullWidth
            label="Setor"
            value={values.departmentId}
            onChange={(event) => onChange({ departmentId: event.target.value })}
            disabled={disabled}
            helperText="Opcional. Sem setor, a instalação entra só no laudo da empresa inteira."
            sx={fieldSx}
          >
            <MenuItem value="">Sem setor</MenuItem>
            {companyDepartments.map((department) => (
              <MenuItem key={department.id} value={String(department.id)}>
                {department.name}
              </MenuItem>
            ))}
          </TextField>
        </Grid>
        <Grid size={{ xs: 12, md: 8 }}>
          <TextField
            fullWidth
            required
            label="Nome da instalação"
            value={values.name}
            onChange={(event) => onChange({ name: event.target.value })}
            error={Boolean(errors.name)}
            helperText={errors.name || 'Ex.: Posto de abastecimento interno'}
            disabled={disabled}
            sx={fieldSx}
          />
        </Grid>
        <Grid size={{ xs: 12, md: 4 }}>
          <TextField
            fullWidth
            label="Identificação interna"
            value={values.tag}
            onChange={(event) => onChange({ tag: event.target.value })}
            error={Boolean(errors.tag)}
            helperText={errors.tag || 'Plaqueta ou patrimônio da empresa'}
            disabled={disabled}
            sx={fieldSx}
          />
        </Grid>
      </Grid>

      <Typography variant="h6" component="h3" sx={{ mb: 2 }}>
        O que ela faz
      </Typography>
      <Grid container spacing={3}>
        <Grid size={{ xs: 12 }}>
          <TextField
            select
            fullWidth
            required
            label="Atividade"
            value={values.activityType}
            onChange={(event) => onChange({ activityType: event.target.value as FlammableActivityType })}
            error={Boolean(errors.activityType)}
            helperText={
              errors.activityType ||
              'A atividade pode definir a classe sozinha. Se for armazenamento ou uso, a classe sai só da capacidade (etapa 2).'
            }
            disabled={disabled}
            sx={fieldSx}
          >
            {FLAMMABLE_ACTIVITY_TYPES.map((type) => (
              <MenuItem key={type} value={type}>
                {ROTULO_DA_ATIVIDADE[type]}
              </MenuItem>
            ))}
          </TextField>
        </Grid>
        {values.activityType === 'OUTRA' && (
          <Grid size={{ xs: 12 }}>
            <TextField
              fullWidth
              required
              label="Descreva a atividade"
              value={values.activityDescription}
              onChange={(event) => onChange({ activityDescription: event.target.value })}
              error={Boolean(errors.activityDescription)}
              helperText={errors.activityDescription || 'Ex.: Oficina com tanque de solvente'}
              disabled={disabled}
              sx={fieldSx}
            />
          </Grid>
        )}
        <Grid size={{ xs: 12 }}>
          <Typography variant="body1" component="p" sx={{ mb: 1, color: 'text.secondary' }}>
            Classe da instalação
          </Typography>
          {classification ? (
            <Box sx={{ minHeight: 44, display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
              <ClassChip classe={classification.class} />
              <Typography variant="body1">{motivoDaClasse(classification)}</Typography>
            </Box>
          ) : (
            <Box sx={{ minHeight: 44, display: 'flex', alignItems: 'center' }}>
              <Typography variant="body1">Calculada ao salvar</Typography>
            </Box>
          )}
          <Typography variant="body1" sx={{ color: 'text.secondary' }}>
            {classification?.activityBelowCapacity
              ? 'A capacidade armazenada sugere uma classe maior do que a da atividade. O laudo registra esse aviso.'
              : 'O sistema define a classe a partir da atividade e do que a instalação armazena.'}
          </Typography>
        </Grid>
        <Grid size={{ xs: 12, md: 8 }}>
          <TextField
            fullWidth
            label="Onde fica"
            value={values.location}
            onChange={(event) => onChange({ location: event.target.value })}
            error={Boolean(errors.location)}
            helperText={errors.location || 'Ex.: Pátio externo, ao lado da portaria 2'}
            disabled={disabled}
            sx={fieldSx}
          />
        </Grid>
        <Grid size={{ xs: 12, md: 4 }}>
          <TextField
            select
            fullWidth
            label="Estado de uso"
            value={values.status}
            onChange={(event) => onChange({ status: event.target.value as FlammableInstallationStatus })}
            disabled={disabled}
            helperText="Instalação desativada não gera pendência no laudo."
            sx={fieldSx}
          >
            {FLAMMABLE_INSTALLATION_STATUSES.map((status) => (
              <MenuItem key={status} value={status}>
                {ROTULO_DO_STATUS_DA_INSTALACAO[status]}
              </MenuItem>
            ))}
          </TextField>
        </Grid>
        <Grid size={{ xs: 12 }}>
          <TextField
            fullWidth
            multiline
            minRows={2}
            label="Observações"
            value={values.observations}
            onChange={(event) => onChange({ observations: event.target.value })}
            helperText="Algo que o laudo deva registrar"
            disabled={disabled}
            sx={fieldSx}
          />
        </Grid>
      </Grid>
    </Box>
  );
};

export default IdentificationStep;
